import React from "react";
import { Box, IconButton, lighten } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { COLORS } from "../../../../../constants";

const iconStyles = {
  color: lighten(COLORS.BLACK, 0.5),
  "&.Mui-disabled": {
    color: lighten(COLORS.BLACK, 0.86),
  },
};

interface Props {
  count: number;
  onPageChange: (e: React.MouseEvent<HTMLButtonElement>, page: number) => void;
  page: number;
  rowsPerPage: number;
  position: string;
}

/**
 * logic for first, back, next, last
 */
const TablePaginationActions = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  position,
}: Props): JSX.Element => {
  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 1);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage)));
  };

  /**
   * display for first, back, next, last
   */
  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 1}
        sx={iconStyles}
        data-testid={`toolbar${position}-first-icon`}
        size="small"
        aria-label="first page"
      >
        <FirstPageIcon fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 1}
        data-testid={`toolbar${position}-back-icon`}
        size="small"
        sx={iconStyles}
        aria-label="previous page"
      >
        <KeyboardArrowLeft fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage)}
        sx={iconStyles}
        data-testid={`toolbar${position}-next-icon`}
        size="small"
        aria-label="next page"
      >
        <KeyboardArrowRight fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage)}
        sx={iconStyles}
        size="small"
        data-testid={`toolbar${position}-last-icon`}
        aria-label="last page"
      >
        <LastPageIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default TablePaginationActions;
